import React, { Component, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { captureError } from '@/config/sentry';
import { analytics } from '@/services/analytics';
import { errorCode, reportBootError } from '@/lib/bootReport';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// FIX-CROP-ERRLOG (E1): the root error boundary. A render throw anywhere under it (a) sends ONE boot-error report
// (src/lib/bootReport.ts → log_client_boot_error → platform_logs, read in AAP > Logs) and (b) shows a visible
// screen — "Something went wrong — Reload" with a short error code — instead of a blank page. No i18n here: this
// screen must render even when the app's own modules failed.
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    try { captureError(error, { componentStack: errorInfo.componentStack }); } catch { /* reporting must never throw */ }
    try { analytics.error('ErrorBoundary', error.message); } catch { /* reporting must never throw */ }
    const firstFrame = (errorInfo.componentStack || '').split('\n').map((l) => l.trim()).filter(Boolean)[0] ?? null;
    void reportBootError('render', error?.message || String(error), error?.stack ?? null, firstFrame, null);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      const message = this.state.error?.message || 'An unexpected error occurred';
      const code = errorCode(message);

      return (
        <div className="min-h-screen flex items-center justify-center p-4" data-testid="boot-error-screen">
          <div className="max-w-md w-full text-center">
            <AlertTriangle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Something went wrong</h1>
            <p className="text-sm text-muted-foreground mb-4">{message}</p>
            <button
              type="button"
              onClick={this.handleReload}
              className="w-full h-12 rounded-lg bg-primary text-primary-foreground text-base font-semibold active:scale-[0.98]"
              data-testid="boot-error-reload"
            >
              Reload
            </button>
            <p className="mt-3 text-xs text-muted-foreground" data-testid="boot-error-code">Error code: {code}</p>

            {import.meta.env.DEV && this.state.error && (
              <div className="mt-4 p-4 bg-muted rounded-lg text-left">
                <p className="text-xs font-mono text-muted-foreground">{this.state.error.stack}</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
